import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export type InvoiceFilterState = {
  from: string;
  to: string;
  status: string;
  search: string;
};

type Props = {
  filters: InvoiceFilterState;
  onChange: (f: InvoiceFilterState) => void;
};

const PRESETS = [
  { label: "This month", key: "this_month" },
  { label: "Last month", key: "last_month" },
  { label: "Last 3 months", key: "last_3m" },
  { label: "YTD", key: "ytd" },
] as const;

function presetDates(key: string): { from: string; to: string } {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (key === "this_month") {
    return {
      from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: fmt(now),
    };
  }
  if (key === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: fmt(start), to: fmt(end) };
  }
  if (key === "last_3m") {
    return {
      from: fmt(new Date(now.getFullYear(), now.getMonth() - 2, 1)),
      to: fmt(now),
    };
  }
  // ytd
  return {
    from: `${now.getFullYear()}-01-01`,
    to: fmt(now),
  };
}

export default function InvoiceFilters({ filters, onChange }: Props) {
  const set = (patch: Partial<InvoiceFilterState>) => onChange({ ...filters, ...patch });

  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex gap-1">
        {PRESETS.map(p => (
          <Button
            key={p.key}
            size="sm"
            variant="outline"
            className="text-xs h-8"
            onClick={() => set(presetDates(p.key))}
          >
            {p.label}
          </Button>
        ))}
      </div>

      <div className="flex items-end gap-2">
        <div className="grid gap-1">
          <Label className="text-xs">From</Label>
          <Input
            type="date"
            className="h-8 text-xs w-36"
            value={filters.from}
            onChange={e => set({ from: e.target.value })}
          />
        </div>
        <div className="grid gap-1">
          <Label className="text-xs">To</Label>
          <Input
            type="date"
            className="h-8 text-xs w-36"
            value={filters.to}
            onChange={e => set({ to: e.target.value })}
          />
        </div>
      </div>

      <div className="grid gap-1">
        <Label className="text-xs">Status</Label>
        <Select value={filters.status} onValueChange={v => set({ status: v })}>
          <SelectTrigger className="h-8 text-xs w-36">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="partially_paid">Partially Paid</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1">
        <Label className="text-xs">Search</Label>
        <Input
          className="h-8 text-xs w-44"
          placeholder="Name, email…"
          value={filters.search}
          onChange={e => set({ search: e.target.value })}
        />
      </div>
    </div>
  );
}
